/**
 * ContactForm — a real submit form that POSTs the enquiry as JSON to a configured
 * endpoint (the Resend email worker — see website/email-worker/). The endpoint
 * holds the Resend API key server-side and emails the team on submit; it runs on
 * any serverless host (Cloudflare Workers, etc.) and needs no GCP.
 *
 * The interest can be pre-selected via ?type= (e.g. /contact?type=enterprise),
 * so the pricing / Enterprise CTAs land here with intent already set. Endpoint is
 * overridable via the @ix:contact-form directive so non-prod hosts can point
 * elsewhere. Composes the shared Input/Textarea/Label/Select/Button primitives.
 */
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../input';
import { Textarea } from '../textarea';
import { Label } from '../label';
import { Button } from '../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { trackEvent } from '../../analytics/track';

export interface ContactFormProps {
  /** Email-worker endpoint that calls Resend. Overridable per host. */
  readonly endpoint?: string;
}

const INTERESTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'beta', label: 'Join the Beta' },
  { value: 'pilot', label: 'Starting a Pilot' },
  { value: 'cloud', label: 'Inferrex Cloud' },
  { value: 'enterprise', label: 'Enterprise (private VPC)' },
  { value: 'sovereign', label: 'Sovereign / air-gapped' },
  { value: 'exploring', label: 'Just exploring' },
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function ContactForm({ endpoint = 'https://inferrex-contact.workers.dev' }: ContactFormProps) {
  const [params] = useSearchParams();
  const initialInterest = INTERESTS.find((i) => i.value === params.get('type'))?.value ?? '';

  const [interest, setInterest] = useState(initialInterest);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const body: Record<string, string> = {
      name: String(data.get('name') ?? '').trim(),
      email: String(data.get('email') ?? '').trim(),
      company: String(data.get('company') ?? '').trim(),
      message: String(data.get('message') ?? '').trim(),
    };
    const systems = String(data.get('systems') ?? '').trim();
    if (systems) body.systems = systems;
    if (interest) body.interest = interest;

    setStatus('submitting');
    setError('');
    try {
      const post = (payload: Record<string, string>) => fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      let res = await post(body);
      if (res.status === 400 && body.interest === 'beta') {
        body.interest = 'exploring';
        body.message = `${body.message}\ninterest_requested: beta`;
        res = await post(body);
      }
      if (!res.ok) throw new Error(res.status === 429 ? 'Too many submissions — please email us instead.' : 'Something went wrong.');
      setStatus('success');
      trackEvent('generate_lead', { form: 'contact', ...(interest ? { interest } : {}) });
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-(--card-radius) border border-border bg-section-secondary p-(--space-card-lg) text-center">
        <p className="font-display text-2xl font-semibold text-foreground">Thanks — we've got it.</p>
        <p className="mt-2 text-muted-foreground">
          We'll come back to you shortly. If it's urgent, email{' '}
          <a href="mailto:hello@inferrex.com" className="text-primary underline-offset-2 hover:underline">hello@inferrex.com</a>.
        </p>
      </div>
    );
  }

  const field = 'space-y-1.5';
  return (
    <form onSubmit={onSubmit} className="space-y-(--space-grid)" noValidate>
      <div className="grid gap-(--space-grid) sm:grid-cols-2">
        <div className={field}>
          <Label htmlFor="cf-name">Name</Label>
          <Input id="cf-name" name="name" required autoComplete="name" placeholder="Your name" />
        </div>
        <div className={field}>
          <Label htmlFor="cf-email">Work email</Label>
          <Input id="cf-email" name="email" type="email" required autoComplete="email" placeholder="you@company.com" />
        </div>
        <div className={field}>
          <Label htmlFor="cf-company">Company</Label>
          <Input id="cf-company" name="company" required autoComplete="organization" placeholder="Company name" />
        </div>
        <div className={field}>
          <Label htmlFor="cf-interest">What brings you?</Label>
          <Select value={interest} onValueChange={setInterest}>
            <SelectTrigger id="cf-interest"><SelectValue placeholder="Choose one" /></SelectTrigger>
            <SelectContent>
              {INTERESTS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className={field}>
        <Label htmlFor="cf-systems">What are you trying to connect? <span className="text-muted-foreground">(optional)</span></Label>
        <Input id="cf-systems" name="systems" placeholder="e.g. Salesforce, a billing system, an internal API" maxLength={1000} />
      </div>
      <div className={field}>
        <Label htmlFor="cf-message">Anything else?</Label>
        <Textarea id="cf-message" name="message" required rows={4} placeholder="Tell us a little about what you're solving." maxLength={5000} />
      </div>
      {status === 'error' && (
        <p className="text-sm text-destructive">
          {error} You can always email <a href="mailto:hello@inferrex.com" className="underline underline-offset-2">hello@inferrex.com</a>.
        </p>
      )}
      <Button type="submit" size="lg" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Send it over'}
      </Button>
    </form>
  );
}
